// ===== API CLIENT =====
//
// A small typed-ish wrapper around the two gallery endpoints, with a single
// error-handling path that reads the universal `{ success, message, errors }`
// envelope and maps status codes to user-facing states.
//
//   POST ${API}/api/events/{slug}/verify-pin   -> { token }
//   GET  ${API}/api/events/{slug}/submissions?page=N  (Bearer token)
//
// The token returned by verify-pin is scoped to ONE event and expires (~1h).
// We keep it in sessionStorage keyed by slug so a refresh within the hour stays
// authenticated, but it never leaks across events or persists long-term.

import { API_BASE } from './config.js';

const TOKEN_PREFIX = 'vgb_token:';

// ---- Per-slug token storage (sessionStorage) ----

export function getToken(slug) {
    try {
        return sessionStorage.getItem(TOKEN_PREFIX + slug) || null;
    } catch {
        return null; // private mode / storage disabled — fall back to in-memory
    }
}

export function setToken(slug, token) {
    try {
        sessionStorage.setItem(TOKEN_PREFIX + slug, token);
    } catch {
        /* ignore — caller still holds the token in memory for this session */
    }
}

export function clearToken(slug) {
    try {
        sessionStorage.removeItem(TOKEN_PREFIX + slug);
    } catch {
        /* ignore */
    }
}

// ---- Error type carrying the parsed envelope + status ----

export class ApiError extends Error {
    constructor(status, message, errors) {
        super(message || `Request failed (HTTP ${status})`);
        this.name = 'ApiError';
        this.status = status;
        this.errors = errors || null;
    }
}

// Parse a Response into either the success payload or a thrown ApiError.
// Every API response is JSON with `{ success, ... }`; we never assume an
// empty/HTML body, and we always prefer the server's `message`.
async function parse(res) {
    let body = null;
    try {
        body = await res.json();
    } catch {
        // Non-JSON (e.g. a proxy/gateway error) — synthesise a message.
        throw new ApiError(res.status, defaultMessageFor(res.status));
    }

    if (res.ok && body && body.success) {
        return body;
    }

    const message = (body && body.message) || defaultMessageFor(res.status);
    throw new ApiError(res.status, message, body && body.errors);
}

function defaultMessageFor(status) {
    switch (status) {
        case 401: return 'Incorrect PIN';
        case 404: return 'This guestbook link isn’t valid.';
        case 422: return 'Validation failed';
        case 429: return 'Too many attempts — please wait a moment and try again.';
        case 500: return 'Something went wrong on the server. Please try again.';
        default:  return `Request failed (HTTP ${status})`;
    }
}

// ---- Endpoints ----

/**
 * Verify the 6-digit PIN for an event and obtain an access token.
 * @param {string} slug
 * @param {string} pin
 * @returns {Promise<string>} the opaque, event-scoped token
 * @throws {ApiError} 401 (bad PIN), 404 (bad slug), 429 (rate-limited)
 */
export async function verifyPin(slug, pin) {
    const res = await fetch(
        `${API_BASE}/api/events/${encodeURIComponent(slug)}/verify-pin`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify({ pin }),
        }
    );
    const body = await parse(res);
    if (!body.token) {
        throw new ApiError(res.status, 'The server did not return an access token.');
    }
    setToken(slug, body.token);
    return body.token;
}

/**
 * Fetch one page of approved submissions for an event.
 * @param {string} slug
 * @param {string} token  event-scoped bearer token from verifyPin()
 * @param {number} page   1-based page index
 * @returns {Promise<{ event: object, data: Array, meta: object }>}
 * @throws {ApiError} 401 (token missing/expired/invalid -> back to PIN gate)
 */
export async function fetchSubmissionsPage(slug, token, page = 1) {
    const res = await fetch(
        `${API_BASE}/api/events/${encodeURIComponent(slug)}/submissions?page=${page}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        }
    );
    const body = await parse(res);
    return {
        event: body.event || null,
        data: Array.isArray(body.data) ? body.data : [],
        meta: body.meta || { current_page: page, last_page: page, total: 0 },
    };
}
